import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { api, FetchError, type ProposalListItem } from '../api.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import type { ProposalStatus } from '@sab/core/schemas';
import { formatDateID } from '@sab/core/utils/format.ts';

interface LibraryProps {
  onOpen: (id: string) => void;
  onNew: () => void;
  publicBaseUrl: string;
}

const TABS: Array<{ key: ProposalStatus | 'all'; label: string }> = [
  { key: 'all', label: 'Semua' },
  { key: 'draft', label: 'Draf' },
  { key: 'published', label: 'Terbit' },
  { key: 'archived', label: 'Arsip' },
];

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'success'; items: ProposalListItem[] };

function statusLabel(s: ProposalStatus): string {
  return { draft: 'Draf', published: 'Terbit', archived: 'Arsip' }[s];
}

export function Library({ onOpen, onNew, publicBaseUrl }: LibraryProps) {
  const [tab, setTab] = useState<ProposalStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [allState, setAllState] = useState<LoadState>({ kind: 'idle' });

  useEffect(() => {
    let cancelled = false;
    setAllState({ kind: 'loading' });
    api
      .list()
      .then(({ items }) => {
        if (!cancelled) setAllState({ kind: 'success', items });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof FetchError ? err.body.error : 'Gagal memuat';
        setAllState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = allState.kind === 'success' ? allState.items : [];
  const counts = useMemo(() => {
    const c = { all: items.length, draft: 0, published: 0, archived: 0 };
    for (const it of items) c[it.status] += 1;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let rows = items;
    if (tab !== 'all') rows = rows.filter((r) => r.status === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) => r.companyName.toLowerCase().includes(q) || r.slug.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [items, tab, search]);

  const monthIso = new Date().toISOString().slice(0, 7);
  const thisMonth = items.filter((it) => it.createdAt.startsWith(monthIso)).length;

  return (
    <section className="page page--library" aria-labelledby="lib-title">
      <header className="lib-header">
        <div className="lib-header__copy">
          <h1 id="lib-title" className="lib-header__title">
            Pustaka Proposal
          </h1>
          <p className="lib-header__lede">
            Susun, sunting, lalu terbitkan deck ke <code>view.sab.id</code>. Setiap proposal punya
            URL unik untuk dibagikan ke prospect.
          </p>
        </div>
        <button className="cta" onClick={onNew} type="button">
          <span>Proposal Baru</span>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </header>

      <section className="kpi" aria-label="Ringkasan">
        <KpiCard label="Total proposal" value={counts.all} tone="ink" />
        <KpiCard label="Draf" value={counts.draft} tone="muted" />
        <KpiCard label="Terbit" value={counts.published} tone="accent" />
        <KpiCard label="Bulan ini" value={thisMonth} tone="muted" />
      </section>

      <div className="lib-toolbar" role="toolbar" aria-label="Filter & cari">
        <div className="tabs tabs--counted" role="tablist" aria-label="Filter status">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`tab ${tab === t.key ? 'tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span>{t.label}</span>
              <span className="tab__count" aria-hidden="true">
                {t.key === 'all' ? counts.all : counts[t.key]}
              </span>
            </button>
          ))}
        </div>
        <label className="search" aria-label="Cari proposal">
          <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="m20 20-3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari berdasarkan perusahaan atau slug…"
            inputMode="search"
            spellCheck={false}
          />
          {search ? (
            <button
              type="button"
              className="search__clear"
              onClick={() => setSearch('')}
              aria-label="Hapus pencarian"
            >
              ×
            </button>
          ) : null}
        </label>
      </div>

      <div className="lib-body" aria-live="polite">
        {allState.kind === 'loading' ? <SkeletonGrid /> : null}

        {allState.kind === 'error' ? (
          <div className="alert alert--error" role="alert">
            {allState.message}
          </div>
        ) : null}

        {allState.kind === 'success' && items.length === 0 ? (
          <EmptyState
            title="Mulai proposal pertama Anda."
            description="Tempel pesan prospect, link website mereka, atau brief singkat. SAB AI akan menyusun deck 25 slide dalam hitungan detik."
            action={
              <button className="cta" onClick={onNew} type="button">
                Mulai Sekarang
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            }
          />
        ) : null}

        {allState.kind === 'success' && items.length > 0 && filtered.length === 0 ? (
          <div className="empty empty--inline">
            <p className="empty__title">Tidak ada hasil</p>
            <p className="empty__desc">Coba ganti kata kunci atau pilih tab lain.</p>
            {search ? (
              <button className="btn" type="button" onClick={() => setSearch('')}>
                Reset pencarian
              </button>
            ) : null}
          </div>
        ) : null}

        {allState.kind === 'success' && filtered.length > 0 ? (
          <ul className="cards" role="list">
            {filtered.map((p) => (
              <li key={p.id}>
                <ProposalCard item={p} onOpen={() => onOpen(p.id)} publicBaseUrl={publicBaseUrl} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ink' | 'accent' | 'muted';
}) {
  return (
    <article className={`kpi__card kpi__card--${tone}`}>
      <p className="kpi__label">{label}</p>
      <p className="kpi__value">{value}</p>
    </article>
  );
}

function ProposalCard({
  item,
  onOpen,
  publicBaseUrl,
}: {
  item: ProposalListItem;
  onOpen: () => void;
  publicBaseUrl: string;
}) {
  const publicUrl =
    item.status === 'published' ? `${publicBaseUrl.replace(/\/$/, '')}/${item.slug}` : null;

  return (
    <article className="card" aria-labelledby={`card-${item.id}-title`}>
      <button
        type="button"
        className="card__hit"
        onClick={onOpen}
        aria-label={`Buka proposal ${item.companyName}`}
      />

      <header className="card__head">
        <span className={`badge badge--${item.status}`}>{statusLabel(item.status)}</span>
        <time className="card__date" dateTime={item.updatedAt}>
          {formatDateID(item.updatedAt)}
        </time>
      </header>

      <h3 id={`card-${item.id}-title`} className="card__title">
        {item.companyName}
      </h3>
      <p className="card__slug">
        <code>{item.slug}</code>
      </p>

      {publicUrl ? <PublicLinkChip url={publicUrl} /> : null}

      <footer className="card__foot">
        <span className="card__author">{item.createdBy}</span>
        <span className="card__arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </footer>
    </article>
  );
}

/**
 * Compact chip on a published card: shows the public URL, click → copy.
 * 3-second checkmark feedback. External-open button sits beside it.
 * Lives ABOVE the .card__hit overlay (z-index) so clicks bypass the open-card hit.
 */
function PublicLinkChip({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const short = url.replace(/^https?:\/\//, '');

  const copy = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — fall back to manual select */
    }
  };

  return (
    <div className="card__pub" role="group" aria-label="Tautan publik">
      <button
        type="button"
        className="card__pub-copy"
        onClick={copy}
        title={copied ? 'Tersalin' : 'Salin tautan publik'}
        aria-live="polite"
      >
        <span className="card__pub-icon" aria-hidden="true">
          {copied ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
              <path
                d="M5 12l4.5 4.5L19 7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none">
              <rect
                x="9"
                y="9"
                width="11"
                height="11"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M5 15V6a2 2 0 0 1 2-2h9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
        <span className="card__pub-url">{short}</span>
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener"
        className="card__pub-open"
        onClick={(e) => e.stopPropagation()}
        title="Buka di tab baru"
        aria-label="Buka tautan publik di tab baru"
      >
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none">
          <path
            d="M14 4h6v6M10 14L20 4M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </a>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <ul className="cards cards--skeleton" aria-label="Memuat" aria-busy="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <li key={i}>
          <div className="card card--skeleton">
            <div className="skel skel--badge" />
            <div className="skel skel--title" />
            <div className="skel skel--line" />
            <div className="skel skel--foot" />
          </div>
        </li>
      ))}
    </ul>
  );
}
